import { type HTMLAttributes, type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PaperClipOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Flex, Space, type TableColumnsType, type TableProps, Tag, Tooltip } from "antd";

import { App } from "@av/components/atoms/app/App";
import { Button } from "@av/components/atoms/Button";
import { Empty } from "@av/components/atoms/Empty";
import { Input } from "@av/components/atoms/input/Input";
import { Table } from "@av/components/atoms/table/Table";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Switch } from "@av/components/molecules/Switch";
import { dayjs } from "@av/config/extended-dayjs";
import { AddDocumentModal } from "./components/AddDocumentModal";
import { DeleteDocumentModal } from "./components/DeleteDocumentModal";
import { EditDocumentModal } from "./components/EditDocumentModal";
import { UploadVersionModal } from "./components/UploadVersionModal";
import { PAGE_SIZE } from "./constants";
import { useCreateCompanyDocument } from "./hooks/useCreateCompanyDocument";
import { useDeleteCompanyDocument } from "./hooks/useDeleteCompanyDocument";
import { useDownloadCompanyDocument } from "./hooks/useDownloadCompanyDocument";
import { useFetchCompanyDocuments } from "./hooks/useFetchCompanyDocuments";
import { useReplaceCompanyDocumentFile } from "./hooks/useReplaceCompanyDocumentFile";
import { useUpdateCompanyDocument } from "./hooks/useUpdateCompanyDocument";
import { toCompanyKnowledgeDocument } from "./map-company-document";
import { useCompanyKnowledgeStyles } from "./styles";

import type {
  CompanyDocumentMutationValues,
  CompanyKnowledgeDocument,
  DocumentFormValues,
  ReplaceCompanyDocumentValues,
} from "./types";

type SortableCompanyKnowledgeField = keyof Pick<
  CompanyKnowledgeDocument,
  "description" | "name" | "uploadedAt"
>;

function compareValue(left?: string, right?: string) {
  return String(left ?? "").localeCompare(String(right ?? ""));
}

function compareField(field: SortableCompanyKnowledgeField) {
  return (left: CompanyKnowledgeDocument, right: CompanyKnowledgeDocument) =>
    compareValue(left[field], right[field]);
}

const formatUploadedDate = (uploadedAt: string) => dayjs(uploadedAt).format("MMM D, YYYY");

export function CompanyKnowledgeSettings() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { styles } = useCompanyKnowledgeStyles();
  const { data: companyDocuments = [], isFetching } = useFetchCompanyDocuments();
  const createCompanyDocument = useCreateCompanyDocument();
  const updateCompanyDocument = useUpdateCompanyDocument();
  const replaceCompanyDocumentFile = useReplaceCompanyDocumentFile();
  const deleteCompanyDocument = useDeleteCompanyDocument();
  const downloadCompanyDocument = useDownloadCompanyDocument();
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<CompanyKnowledgeDocument | undefined>();
  const [deletingDocument, setDeletingDocument] = useState<CompanyKnowledgeDocument | undefined>();
  const [renewingDocument, setRenewingDocument] = useState<CompanyKnowledgeDocument | undefined>();

  const documents = useMemo(
    () => companyDocuments.map(toCompanyKnowledgeDocument),
    [companyDocuments]
  );

  const filteredDocuments = useMemo(() => {
    const normalizedSearchText = searchText.trim().toLocaleLowerCase();

    if (!normalizedSearchText) {
      return documents;
    }

    return documents.filter((document) =>
      [document.name, document.description, document.fileName].some((value) =>
        value.toLocaleLowerCase().includes(normalizedSearchText)
      )
    );
  }, [documents, searchText]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredDocuments.length / PAGE_SIZE));

    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [currentPage, filteredDocuments.length]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleTableChange: TableProps<CompanyKnowledgeDocument>["onChange"] = (pagination) => {
    setCurrentPage(pagination.current ?? 1);
  };

  const handleToggleUseInAva = (documentId: string, useInAva: boolean) => {
    updateCompanyDocument.mutate({ documentId, values: { useInAva } });
  };

  const getSuccessMessage = ({
    successMessage,
    useInAva,
  }: {
    successMessage: string;
    useInAva: boolean;
  }): ReactNode => {
    if (!useInAva) {
      return successMessage;
    }

    return (
      <span>
        <span>{successMessage}</span>
        <span style={{ display: "block" }}>
          {t("platform.adminSettings.companyKnowledge_successAvaSource")}
        </span>
      </span>
    );
  };

  const handleDelete = async (document: CompanyKnowledgeDocument) => {
    await deleteCompanyDocument.mutateAsync(document.id);
    setDeletingDocument(undefined);
    message.success(
      t("platform.adminSettings.companyKnowledge_deleteSuccess", { name: document.name })
    );
  };

  const handleAdd = async (values: CompanyDocumentMutationValues) => {
    const createdDocument = await createCompanyDocument.mutateAsync(values);

    setCurrentPage(1);
    setIsAddModalOpen(false);
    message.success(
      getSuccessMessage({
        successMessage: t("platform.adminSettings.companyKnowledge_addSuccess", {
          name: createdDocument.name,
        }),
        useInAva: createdDocument.useInAva,
      })
    );
  };

  const handleSaveEdit = async (
    values: Pick<DocumentFormValues, "name" | "description" | "useInAva">
  ) => {
    const documentId = editingDocument?.id;

    if (!documentId) {
      return;
    }

    const updatedDocument = await updateCompanyDocument.mutateAsync({
      documentId,
      values: {
        name: values.name,
        description: values.description,
        useInAva: values.useInAva,
      },
    });

    setEditingDocument(undefined);
    message.success(
      getSuccessMessage({
        successMessage: t("platform.adminSettings.companyKnowledge_saveSuccess", {
          name: updatedDocument.name,
        }),
        useInAva: updatedDocument.useInAva,
      })
    );
  };

  const handleRenew = async (values: ReplaceCompanyDocumentValues) => {
    await replaceCompanyDocumentFile.mutateAsync(values);
    setRenewingDocument(undefined);
    message.success(
      getSuccessMessage({
        successMessage: t("platform.adminSettings.companyKnowledge_uploadNewVersionSuccess", {
          name: values.document.name,
        }),
        useInAva: values.document.useInAva,
      })
    );
  };

  const headerCell = (testId: string) => () =>
    ({ "data-testid": testId }) as HTMLAttributes<HTMLElement>;

  const columns: TableColumnsType<CompanyKnowledgeDocument> = [
    {
      title: t("platform.adminSettings.companyKnowledge_columnName"),
      dataIndex: "name",
      key: "name",
      defaultSortOrder: "ascend",
      sorter: compareField("name"),
      onHeaderCell: headerCell("company-knowledge-column-name"),
      width: 180,
    },
    {
      title: t("platform.adminSettings.companyKnowledge_columnDescription"),
      dataIndex: "description",
      key: "description",
      sorter: compareField("description"),
      onHeaderCell: headerCell("company-knowledge-column-description"),
      width: 260,
    },
    {
      title: t("platform.adminSettings.companyKnowledge_columnDateUploaded"),
      dataIndex: "uploadedAt",
      key: "dateUploaded",
      sorter: compareField("uploadedAt"),
      onHeaderCell: headerCell("company-knowledge-column-date-uploaded"),
      width: 180,
      render: (uploadedAt: string, document) => (
        <Space>
          {formatUploadedDate(uploadedAt)}
          {document.renewed && (
            <Tag data-testid={`company-knowledge-renewed-${document.id}`}>
              {t("platform.adminSettings.companyKnowledge_renewed")}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: (
        <Flex align="center" gap={8}>
          <span>{t("platform.adminSettings.companyKnowledge_columnUseInAva")}</span>
          <InfoCircleOutlined className={styles.actionIcon} />
        </Flex>
      ),
      dataIndex: "useInAva",
      key: "useInAva",
      onHeaderCell: headerCell("company-knowledge-column-use-in-ava"),
      width: 220,
      render: (useInAva: boolean, document) => (
        <Switch
          aria-label={t("platform.adminSettings.companyKnowledge_toggleAva", {
            name: document.name,
          })}
          checked={useInAva}
          loading={updateCompanyDocument.isPending}
          onChange={(checked) => handleToggleUseInAva(document.id, checked)}
          testIdPrefix={`company-knowledge-ava-access-${document.id}`}
        />
      ),
    },
    {
      title: t("platform.adminSettings.companyKnowledge_columnFile"),
      dataIndex: "fileName",
      key: "fileName",
      onHeaderCell: headerCell("company-knowledge-column-file"),
      width: 220,
      render: (fileName: string, document) => (
        <Space>
          <PaperClipOutlined />
          <Button
            className={styles.fileButton}
            onClick={() => downloadCompanyDocument.mutate(document.id)}
            testIdPrefix={`company-knowledge-file-${document.id}`}
            type="link"
          >
            {fileName}
          </Button>
        </Space>
      ),
    },
    {
      title: t("platform.adminSettings.companyKnowledge_columnActions"),
      key: "actions",
      align: "center",
      onHeaderCell: headerCell("company-knowledge-column-actions"),
      width: 150,
      render: (_, document) => (
        <Space>
          <Tooltip title={t("platform.adminSettings.companyKnowledge_edit")}>
            <Button
              aria-label={t("platform.adminSettings.companyKnowledge_editDocumentAria", {
                name: document.name,
              })}
              icon={<EditOutlined className={styles.actionIcon} />}
              onClick={() => setEditingDocument(document)}
              testIdPrefix={`company-knowledge-edit-${document.id}`}
              type="text"
            />
          </Tooltip>
          <Tooltip title={t("platform.adminSettings.companyKnowledge_uploadNewVersion")}>
            <Button
              aria-label={t("platform.adminSettings.companyKnowledge_renewDocumentAria", {
                name: document.name,
              })}
              icon={<ReloadOutlined className={styles.actionIcon} />}
              onClick={() => setRenewingDocument(document)}
              testIdPrefix={`company-knowledge-renew-${document.id}`}
              type="text"
            />
          </Tooltip>
          <Button
            aria-label={t("platform.adminSettings.companyKnowledge_deleteDocumentAria", {
              name: document.name,
            })}
            icon={<DeleteOutlined className={styles.actionDeleteIcon} />}
            onClick={() => setDeletingDocument(document)}
            testIdPrefix={`company-knowledge-delete-${document.id}`}
            type="text"
          />
        </Space>
      ),
    },
  ];

  return (
    <section className={styles.contentCard} data-testid="company-knowledge-section">
      <Flex align="center" justify="space-between">
        <Typography.Title level={4} testIdPrefix="company-knowledge-title">
          {t("platform.adminSettings.companyKnowledge")}
        </Typography.Title>
        <Button
          color="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAddModalOpen(true)}
          testIdPrefix="company-knowledge-add-document"
          variant="outlined"
        >
          {t("platform.adminSettings.companyKnowledge_addDocumentButton")}
        </Button>
      </Flex>
      <Typography.Text className={styles.helperText} testIdPrefix="company-knowledge-helper">
        {t("platform.adminSettings.companyKnowledge_helper")}
      </Typography.Text>
      {!isFetching && documents.length === 0 ? (
        <Flex align="center" justify="center" style={{ minHeight: 180 }}>
          <Empty
            description={t("platform.adminSettings.companyKnowledge_emptyTitle")}
            isDescriptionBold
            secondaryDescription={t("platform.adminSettings.companyKnowledge_emptyDescription")}
            testIdPrefix="company-knowledge"
          />
        </Flex>
      ) : (
        <>
          <Flex className={styles.searchRow} justify="flex-start">
            <Input.Search
              allowClear
              className={styles.searchInput}
              onChange={(event) => handleSearch(event.target.value)}
              placeholder={t("platform.adminSettings.companyKnowledge_search")}
              testIdPrefix="company-knowledge-search"
              value={searchText}
            />
          </Flex>
          <div className={styles.table}>
            <Table
              columns={columns}
              dataSource={filteredDocuments}
              loading={isFetching}
              onChange={handleTableChange}
              pagination={{
                current: currentPage,
                onChange: setCurrentPage,
                pageSize: PAGE_SIZE,
                showSizeChanger: false,
                showTotal: (total, range) => (
                  <Typography.Text
                    className={styles.paginationSummary}
                    testIdPrefix="company-knowledge-pagination-summary"
                  >
                    {t("platform.adminSettings.companyKnowledge_paginationSummary", {
                      count: total === 0 ? 0 : range[1] - range[0] + 1,
                      total,
                    })}
                  </Typography.Text>
                ),
                total: filteredDocuments.length,
              }}
              rowKey="id"
              scroll={{ x: 1200 }}
              testIdPrefix="company-knowledge"
            />
          </div>
        </>
      )}
      {isAddModalOpen && (
        <AddDocumentModal
          isSaving={createCompanyDocument.isPending}
          onAdd={handleAdd}
          onCancel={() => setIsAddModalOpen(false)}
          open={isAddModalOpen}
        />
      )}
      {editingDocument && (
        <EditDocumentModal
          document={editingDocument}
          isSaving={updateCompanyDocument.isPending}
          onCancel={() => setEditingDocument(undefined)}
          onSave={handleSaveEdit}
        />
      )}
      <DeleteDocumentModal
        document={deletingDocument}
        isDeleting={deleteCompanyDocument.isPending}
        onCancel={() => setDeletingDocument(undefined)}
        onDelete={handleDelete}
      />
      {renewingDocument && (
        <UploadVersionModal
          document={renewingDocument}
          isSaving={replaceCompanyDocumentFile.isPending}
          onCancel={() => setRenewingDocument(undefined)}
          onRenew={handleRenew}
        />
      )}
    </section>
  );
}
